"use client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { API_ROUTES } from "utils/api-route.constants";
import {
  CityMatchedCardsRequest,
  CityMatchedCardsResponse,
} from "modules/city/model/cityCards";
import { INFINITE_ALWAYS_FRESH_CONFIG } from "modules/common/react-query-configs";
import { useAuth } from "@clerk/nextjs";

export const useInfiniteCityMatchedCards = (
  req: CityMatchedCardsRequest,
  options?: { enabled?: boolean }
) => {
  const fetch = useFetchWrapper();
  const { isSignedIn, isLoaded, userId } = useAuth();
  const {
    data,
    isSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isPending,
    isFetching,
    isError,
  } = useInfiniteQuery<CityMatchedCardsResponse>({
    queryKey: [QUERIES.CITY_MATCHED_CARDS, userId, req],
    queryFn: ({ pageParam }) =>
      getMatchedCards({
        ...req,
        page: pageParam as number,
      }),
    enabled: (options?.enabled ?? true) && isLoaded && isSignedIn && !!userId,
    initialPageParam: 1,
    ...INFINITE_ALWAYS_FRESH_CONFIG, // Always-fresh: drain-debounced reads, no caching
    // Hold rows across filter changes only for the same viewer and city. A
    // user-keyed query alone is insufficient because placeholderData can still
    // receive the previous account's query while Clerk switches sessions.
    placeholderData: (previous, previousQuery) => {
      const previousUserId = previousQuery?.queryKey[1];
      const previousReq = previousQuery?.queryKey[2] as
        | CityMatchedCardsRequest
        | undefined;
      return previousUserId === userId &&
        previousReq?.latitude === req.latitude &&
        previousReq?.longitude === req.longitude
        ? previous
        : undefined;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.length + 1;
      } else {
        return undefined;
      }
    },
  });

  return {
    isLoading,
    isSuccess,
    isFetching,
    data,
    isFetchingNextPage,
    isPending,
    isError,
    totalResults: data?.pages[0].total,
    currentCount:
      data?.pages.reduce((acc, page) => acc + page.items.length, 0) ?? 0,
    hasNextPage,
    fetchNextPage,
  };

  async function getMatchedCards(reg: CityMatchedCardsRequest) {
    // Build query parameters
    const params = new URLSearchParams({
      latitude: reg.latitude,
      longitude: reg.longitude,
      page: reg.page.toString(),
      per_page: reg.perPage.toString(),
    });

    // Add search query if provided
    if (reg.query && reg.query.trim() !== "") {
      params.append("query", reg.query);
    }

    return fetch
      .get(API_ROUTES.TRADE.CITY_MATCHED_CARDS + `?${params.toString()}`, null)
      .then((res: CityMatchedCardsResponse) => res);
  }
};
